/**
 * Phase 30.5: per-project ID prefixing.
 *
 * Problem fixed
 * -------------
 * Risk and Change Request IDs were generated as `RSK-001` / `CR-001` —
 * sequential per project, but globally unique by `id` alone in the database
 * (the primary key is just `id`, and upserts use `?on_conflict=id`). When a
 * user added a risk in Project A, then a risk in Project B, both got
 * `RSK-001` — but Project A's `RSK-001` already existed in the database, so
 * Project B's upsert resolved to an UPDATE of A's row, which RLS correctly
 * rejected (since the operation amounts to moving A's risk to B's project).
 *
 * The fix
 * -------
 * Prefix newly-generated IDs with the project's suffix. For project
 * `PRJ-SAFXUT`, a new risk becomes `SAFXUT-RSK-001`. The prefix guarantees
 * uniqueness across projects without any database changes — `SAFXUT-RSK-001`
 * cannot exist anywhere else because no other project has the `SAFXUT-`
 * prefix.
 *
 * Existing data is left alone. Old short-form IDs (`RSK-001`, `CR-001`)
 * keep working as-is; they're already valid rows. Only NEW IDs use the
 * prefixed form. This means a project that already has `RSK-001` will get
 * `SAFXUT-RSK-001` as its next risk — the two coexist with different ID
 * shapes, which is mildly inconsistent but acceptable as a launch-fast
 * tradeoff.
 *
 * Scope: risks and change_requests only. Issues already use random 6-char
 * IDs (ISS-XXXXXX) and baselines use timestamp-based IDs — both
 * collision-resistant by design, no bug.
 *
 * Known limitation: two users adding a risk to the SAME project at the
 * exact same moment could still pick the same next-counter and collide.
 * That race window is tiny and essentially non-existent at current scale.
 * A future schema migration to composite primary keys (option A1) would
 * eliminate it. Out of scope here.
 */

/**
 * Extract the human-readable prefix from a project ID.
 *
 * Examples:
 *   "PRJ-SAFXUT"   -> "SAFXUT"
 *   "PRJ-J5IOJP"   -> "J5IOJP"
 *   "PRJ-SAMPLE-1" -> "SAMPLE-1"      (preserves anything after the first PRJ-)
 *   "anything"     -> "anything"      (returns the input if no PRJ- prefix)
 *
 * Always returns a non-empty string. Designed to be defensive against
 * unexpected project ID shapes.
 */
export function projectIdPrefix(projectId: string): string {
  if (!projectId) return 'P';
  // Strip leading "PRJ-" if present; otherwise use the whole id.
  const stripped = projectId.startsWith('PRJ-') ? projectId.slice(4) : projectId;
  return stripped || projectId;
}

/**
 * Compute the next sequential ID for a per-project numbered entity.
 *
 * @param existing  current entities in this project (their `id` fields are inspected)
 * @param prefix    the per-project full prefix, e.g. "SAFXUT-RSK"
 * @returns         the next ID, e.g. "SAFXUT-RSK-001"
 *
 * Counting rule: looks only at existing IDs that START WITH `${prefix}-`,
 * so old short-form IDs (e.g. `RSK-001`) are ignored when picking the next
 * number. That means the very first new ID in a project that already has
 * old `RSK-001` rows will be `${prefix}-001` — old and new coexist.
 */
export function nextProjectScopedId(
  existing: ReadonlyArray<{ id: string }>,
  prefix: string,
): string {
  const fullPrefix = `${prefix}-`;
  const max = existing.reduce((m, e) => {
    if (!e.id.startsWith(fullPrefix)) return m;
    const tail = e.id.slice(fullPrefix.length);
    const n = parseInt(tail, 10);
    return Number.isNaN(n) ? m : Math.max(m, n);
  }, 0);
  return `${prefix}-${String(max + 1).padStart(3, '0')}`;
}
